import { defineComponent, nextTick, onBeforeUnmount, onMounted } from 'vue';
import ResizeObserver from 'resize-observer-polyfill';

import { getCurrentInstance, IN_BROWSER, noop } from '@components/utils';

import type { PropType } from 'vue';

const hasObserver = typeof ResizeObserver !== 'undefined';

export const LayoutResizeObserver = defineComponent({
  name: 'CdsLayoutResizeObserver',
  props: {
    debounce: {
      type: [String, Number] as PropType<string | number>,
      default: 100
    }
  },
  emits: {
    resize: (contentRect: Pick<DOMRectReadOnly, 'width' | 'height'>) => true
  },
  setup(props, { emit }) {
    if (!IN_BROWSER) return noop;

    let timer: any = null;
    let targetEl: HTMLElement | undefined;
    let size: Pick<DOMRectReadOnly, 'width' | 'height'> = { width: -1, height: -1 };

    function trigger(_entries: ResizeObserverEntry[], _observer: ResizeObserver, immediately?: boolean) {
      if (immediately || props.debounce === 0 || props.debounce === '0') {
        emitEvent();
      } else if (timer === null) {
        timer = setTimeout(emitEvent, Number(props.debounce));
      }
    }

    function emitEvent() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }

      if (targetEl) {
        const { offsetWidth: width, offsetHeight: height } = targetEl;

        if (width !== size.width || height !== size.height) {
          size = { width, height };
          emit('resize', size);
        }
      }
    }

    const { proxy } = getCurrentInstance('resize-observer');

    if (!hasObserver) return noop;

    let observer: ResizeObserver | undefined;

    const init = (stop?: boolean) => {
      targetEl = proxy?.$el.parentNode;

      if (targetEl) {
        observer = new ResizeObserver(trigger);
        observer.observe(targetEl);
        emitEvent();
      } else if (!stop) {
        nextTick(() => {
          init(true);
        });
      }
    };

    onMounted(() => {
      init();
    });

    onBeforeUnmount(() => {
      timer !== null && clearTimeout(timer);

      if (observer !== undefined) {
        if (observer.disconnect !== undefined) {
          observer?.disconnect();
        } else if (targetEl) {
          observer.unobserve(targetEl);
        }
      }
    });

    return noop;
  }
});
